import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { UserService } from '@services/UserService';

import { toastifyApiErrors } from '@utils';
import type { UserForm } from '@flows/private/users/pages/UserDetails/components/UserEditForm/types';
import { useUserActionsState } from '@flows/private/users/pages/UserDetails/components/UserEditForm/hooks';
import { UserActionsOptionsEnum } from '@flows/private/users/pages/UserDetails/components/UserEditForm/constants/userActionsOptions';
import { useAccessManagement } from 'components/ProfileSelectionCard/hooks';
import { useAuth } from '@hooks';
import { useUserDetailsState } from '@flows/private/users/pages/UserDetails/hooks';

import { handleEditUserProfiles } from './utils';
import type { EditUserProps } from './types';

export function useEditUser({
  formState: { dirtyFields },
}: Readonly<EditUserProps>) {
  const { selectedCompany } = useAuth();
  const [updatedSuccessfullyOpen, setUpdatedSuccessfullyOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();
  const { changeCurrentAction } = useUserActionsState();
  const {
    editedPartnerCompanies,
    availableProfilesState,
    originalPartnerCompanies,
    selectedAllProfileId,
    selectedProfiles,
  } = useAccessManagement();
  const { userDetailsState } = useUserDetailsState();

  const companyId = selectedCompany?.id ?? '';

  const { profiles: initialProfiles } = userDetailsState?.data?.content ?? {};

  const handleCloseUpdatedSuccessfully = () =>
    setUpdatedSuccessfullyOpen(false);

  const handleUpdateUser = useCallback(
    async (data: UserForm) => {
      try {
        changeCurrentAction(UserActionsOptionsEnum.EDIT);

        const values = {
          document: data?.document,
          email: data?.email,
          phone: data?.phone,
          name: data?.name,
          companyId,
          profiles: handleEditUserProfiles(
            editedPartnerCompanies,
            originalPartnerCompanies,
            availableProfilesState,
            selectedProfiles,
            selectedAllProfileId,
            initialProfiles,
          ),
        };

        if (id) {
          const userService = new UserService();

          await userService.updateUser(id, values);

          if (dirtyFields?.email) setUpdatedSuccessfullyOpen(true);

          return true;
        }
      } catch (error) {
        toastifyApiErrors(error, enqueueSnackbar);
      } finally {
        changeCurrentAction(undefined);
      }
      return false;
    },
    [
      enqueueSnackbar,
      id,
      changeCurrentAction,
      dirtyFields,
      editedPartnerCompanies,
      availableProfilesState,
      originalPartnerCompanies,
    ],
  );

  return {
    handleCloseUpdatedSuccessfully,
    updatedSuccessfullyOpen,
    handleUpdateUser,
  };
}
